import type { ScholarshipListItem } from '../../../../types/scholarships';

/*
  Board order is ONE global sequence, and the page renders two sections of it.

  The API stores a single `sortOrder` per board rather than one per purpose, so Roster Management
  and Recruiting Board are two windows onto the same list. That is why these helpers always hand
  back the whole list: a reorder inside one section has to be expressed as a change to the global
  order, and the reorder request carries every board's id.

  Moving a board within its section leaves every other board exactly where it was, including the
  boards of the other kind interleaved between them. That falls out of writing the section's new
  order back into the global slots its own members already occupied, rather than splicing the row
  into a new global index and dragging everything else along with it.
*/

/** The same list with the item at `from` moved to `to`. Out-of-range indexes change nothing. */
export const moveItem = <T,>(items: readonly T[], from: number, to: number): T[] => {
  const next = [...items];
  if (from < 0 || to < 0 || from >= next.length || to >= next.length || from === to) return next;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * The whole list, with one section's members re-sequenced into `orderedGroupIds` and every other
 * board left on the index it already held.
 *
 * An id in `orderedGroupIds` that is not in `all`, or a section member the new order forgets, is
 * refused outright (the list comes back unchanged) rather than half-applied: a partial write here
 * would be sent to the API as the coach's intended order.
 */
export const applySectionOrder = (
  all: readonly ScholarshipListItem[],
  orderedGroupIds: readonly string[],
): ScholarshipListItem[] => {
  const wanted = new Set(orderedGroupIds);
  const slots: number[] = [];
  all.forEach((s, index) => {
    if (wanted.has(s.id)) slots.push(index);
  });
  if (slots.length !== orderedGroupIds.length) return [...all];

  const byId = new Map(all.map((s) => [s.id, s]));
  const next = [...all];
  orderedGroupIds.forEach((id, i) => {
    const item = byId.get(id);
    if (item) next[slots[i]] = item;
  });
  return next;
};

/**
 * The ids to send with a reorder. The API requires the set to match the caller's non-archived
 * boards exactly, so archived rows are dropped here rather than at the call site — a list loaded
 * with "Show archived" on carries both kinds.
 */
export const reorderPayloadIds = (all: readonly ScholarshipListItem[]): string[] =>
  all.filter((s) => !s.isArchived).map((s) => s.id);
